import React from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import Icon from 'components/design-system/Icon'
import Tooltip from 'components/design-system/Tooltip'

const STATUS = {
  done: { icon: 'check', bar: 'var(--color-status-success-fg)' },
  failed: { icon: 'close', bar: 'var(--color-text-danger)' },
  uploading: { icon: 'autorenew', bar: 'var(--color-bg-accent)' }
}

/**
 * Upload Progress Item (Figma 108:19101): tên file (Caption/12) + status icon 16 + progress bar 2px.
 * `status`: done | failed | uploading. `progress` (0–100) chỉ dùng khi uploading; done / failed = đầy thanh.
 * `onCancel` (tuỳ chọn, chỉ khi uploading): hover vào icon đổi thành nút huỷ.
 */
const UploadProgressItem = ({ name, status = 'uploading', progress = 0, onCancel, sx }) => {
  const s = STATUS[status]
  const value = status === 'uploading' ? Math.min(100, Math.max(0, progress)) : 100
  const cancellable = status === 'uploading' && onCancel

  return (
    <Box
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', width: '100%', py: 'var(--space-4)' },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)' }}>
        <Box
          title={name}
          sx={{
            flex: '1 0 0',
            minWidth: 0,
            fontFamily: 'var(--font-family-primary)',
            fontWeight: 'var(--font-weight-regular)',
            fontSize: 'var(--font-size-12)', // Caption/12 Regular
            lineHeight: 'normal',
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {name}
        </Box>
        {cancellable ? (
          <Tooltip title="Cancel upload">
            <Box
              component="button"
              type="button"
              aria-label="Cancel upload"
              onClick={onCancel}
              sx={{
                display: 'flex',
                p: 0,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                color: 'var(--color-icon-default)',
                '& .icon-cancel': { display: 'none' },
                '&:hover .icon-status, &:focus-visible .icon-status': { display: 'none' },
                '&:hover .icon-cancel, &:focus-visible .icon-cancel': { display: 'block' },
                '&:hover': { color: 'var(--color-icon-strong)' },
                '&:focus-visible': { outline: '2px solid var(--color-border-focus)', outlineOffset: '2px' }
              }}
            >
              <Icon name={s.icon} size={16} className="icon-status" />
              <Icon name="close" size={16} className="icon-cancel" />
            </Box>
          </Tooltip>
        ) : (
          <Icon name={s.icon} size={16} color="var(--color-icon-default)" />
        )}
      </Box>
      <Box
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{ height: 2, width: '100%', overflow: 'hidden', bgcolor: 'var(--color-border-subtle)', borderRadius: 'var(--radius-full)' }}
      >
        <Box sx={{ height: '100%', width: `${value}%`, bgcolor: s.bar, transition: 'width 0.3s ease' }} />
      </Box>
    </Box>
  )
}

UploadProgressItem.propTypes = {
  name: PropTypes.node,
  status: PropTypes.oneOf(['done', 'failed', 'uploading']),
  progress: PropTypes.number,
  onCancel: PropTypes.func,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default UploadProgressItem
